<template>
  <div class="flex items-center justify-between" :class="headerClasses">
    <div
      class="flex min-w-0 items-center space-x-3 pb-4"
      :class="[
        isDraggableState ? 'handle grow cursor-grab pr-2 pl-10' : 'pl-4',
      ]"
    >
      <div
        class="text-md mb-0 flex space-x-1 truncate text-left font-semibold"
        :class="isDraggableState ? 'text-white' : 'text-headline'"
      >
        <div v-if="isEmailThreadOpen" class="flex items-center space-x-2">
          <button
            @click.stop="emit('activity-type:changed', storedActiveTypes)"
          >
            <icon-base
              :icon="IconCaretDown"
              class="transform text-label rotate-90"
            />
          </button>
          <span>{{ activity.title }}</span>
        </div>
        <span v-else-if="isDealsList">{{ activity.title }}</span>
        <activity-selector
          v-else
          :active-types="activity.types"
          :title="activity.title"
          class="selector-draggable"
          @activity-type:changed="emit('activity-type:changed', $event)"
        />
        <div
          v-if="(isDealsList && !loading) || isDealActivityPipModeActivated"
          class="space-x-1 truncate"
        >
          <span class="font-light">{{ $t("COMMON.FOR") }}</span>
          <span
            v-if="businessLegalName"
            v-tooltip="{
              content: businessLegalName,
              onShow: () => !isDealActivityPipModeActivated,
            }"
          >
            {{ businessLegalName }}
          </span>
          <span v-else>-</span>
        </div>
      </div>
      <div
        v-if="activity.total && !loading && !isEmailThreadOpen"
        class="leading-medium flex h-[17px] justify-center rounded-full bg-divider px-1-25 pt-px text-xxs font-semibold text-gray-800"
      >
        {{ activity.total }}
      </div>
    </div>
    <div class="flex items-center space-x-2 p-4 pt-0">
      <div
        class="rounded px-1 py-0-5"
        :class="{ 'bg-primary-background-active': isSearchActive }"
        @click="emit('search:toggle')"
      >
        <icon-base
          height="16"
          width="16"
          view-box="0 0 18 18"
          :icon="IconMagnifyingGlass"
          icon-name="Search"
          class="cursor-pointer"
          :class="searchIconClass"
        />
      </div>
      <icon-base
        v-if="isDraggableState && !isDealActivityPipModeActivated"
        data-cy="minimize-restore-activity"
        v-bind="minimizeOptions"
        class="cursor-pointer text-white"
        @click="emit('minimize:toggle')"
      />
      <div
        v-if="!isDealsList && !isDealActivityPipModeActivated"
        v-tooltip="
          isDraggableState
            ? $t('COMMON.SHOW_AS_SIDEBAR')
            : $t('COMMON.SHOW_AS_POPOVER')
        "
        data-cy="init-popover"
        class="cursor-pointer"
        @click="emit('popover:init', $event)"
      >
        <icon-base :icon="IconSmallWindow" />
      </div>
      <icon-base
        v-if="!isDealActivityPipModeActivated"
        data-cy="close-activity"
        title="close-activity"
        icon="x"
        width="20"
        height="20"
        icon-color="none"
        icon-name="Close"
        class="!mb-px cursor-pointer"
        :class="isDraggableState ? 'text-white' : 'text-gray-400'"
        @click="emit('activity:close')"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";

import { storeToRefs } from "pinia";

import { DealActivityState } from "@/enums/dealActivity";
import useNotesStore from "@/stores/notes";

import ActivitySelector from "@/components/deals/dealActivity/ActivitySelector.vue";
import IconMagnifyingGlass from "@/components/icons/IconMagnifyingGlass.vue";
import IconSmallWindow from "@/components/icons/IconSmallWindow.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconWidget from "@/components/icons/IconWidget.vue";
import IconCaretDown from "@/components/icons/deals/IconCaretDown.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { CommunicationType } from "@/enums/communicationLogs";
import type { DealActivityOptions } from "@/models/common";

const {
  activity,
  loading,
  isDealsList,
  businessLegalName,
  isSearchActive,
  options,
  isEmailThreadOpen,
  storedActiveTypes,
} = defineProps<{
  activity: {
    total: number;
    types: CommunicationType[];
    title: string;
  };
  businessLegalName?: string;
  isDealsList: boolean;
  isSearchActive: boolean;
  options: DealActivityOptions;
  loading: boolean;
  isEmailThreadOpen: boolean;
  storedActiveTypes: CommunicationType[];
}>();

const emit = defineEmits<{
  "search:toggle": [];
  "minimize:toggle": [];
  "popover:init": [MouseEvent];
  "pip:activate": [];
  "activity:close": [];
  "activity-type:changed": [CommunicationType[]];
}>();

const notesStore = useNotesStore();
const { isDealActivityPipModeActivated } = storeToRefs(notesStore);

const headerClasses = computed(() => {
  if (options.state === DealActivityState.PiP) {
    return "bg-grey-navigation text-white pt-4";
  }
  if (options.state === DealActivityState.Static) {
    return "border-b border-gray-300/50";
  }
  let draggableClass = "bg-grey-navigation text-white pt-4";
  if (options.minimized) {
    draggableClass += " rounded-md";
  } else {
    draggableClass += " rounded-t-md";
  }
  return draggableClass;
});

const minimizeOptions = computed(() =>
  options.minimized
    ? {
        icon: IconWidget,
        iconName: "Restore",
        viewBox: "0 0 16 14",
      }
    : {
        icon: IconSubtract,
        iconName: "Minimize",
        viewBox: "0 -5 20 20",
      }
);

const isDraggableState = computed(
  () => options.state === DealActivityState.Draggable
);

const searchIconClass = computed(() => {
  if (isSearchActive) {
    return "text-primary";
  }
  return isDraggableState.value ? "text-white" : "text-gray-400";
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.selector-draggable :deep(.dropdown) {
  @apply fixed z-[1000];
}
</style>
